import { useState } from 'react';

import { keepPreviousData, useMutation } from '@tanstack/react-query';
import { flexRender } from '@tanstack/react-table';
import { format, parse } from 'date-fns';
import { EditIcon, EyeIcon, PlusIcon, UploadIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import DialogExportRecords from '../../../../components/pages/backoffice/dialogs/export-records.jsx';
import Error from '../../../../components/feedback/error.jsx';
import Loading from '../../../../components/feedback/loading.jsx';
import { DataTableColumnHeader } from '../../../../components/table/data-table-column-header.jsx';
import { DataTableFooter } from '../../../../components/table/data-table-footer.jsx';
import {
  DataTableFacetedFilter,
  DataTableToolbar,
} from '../../../../components/table/data-table-toolbar.jsx';
import Heading from '../../../../components/typography/heading.jsx';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '../../../../components/ui/avatar.jsx';
import { Badge } from '../../../../components/ui/badge.jsx';
import { Button } from '../../../../components/ui/button.jsx';
import { Checkbox } from '../../../../components/ui/checkbox.jsx';
import { DebouncedInput } from '../../../../components/ui/input-debounced.jsx';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../../components/ui/table.jsx';
import { msCatalystApi } from '../../../../services/api/ms-catalyst-api.js';
import { useTable } from '../../../../hooks/use-table.jsx';
import { TABLE_FILTER_LIKE } from '../../../../utils/constants.js';
import { getExportFile } from '../../../../utils/files.js';
import { filterIgnoreAccents } from '../../../../utils/filters.js';

const TABLE_COLUMNS = [
  {
    id: 'select',
    header: ({ table }) => (
      <div className="pr-2">
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && 'indeterminate')
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Selecionar tudo"
          className="translate-y-[2px]"
        />
      </div>
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Selecionar linha"
        className="translate-y-[2px]"
      />
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    title: 'Número',
    accessorKey: 'number',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Número" />
    ),
    cell: ({ row }) => <div>{row.getValue('number')}</div>,
    enableSorting: false,
    enableHiding: false,
  },
  {
    title: 'Imagem',
    accessorKey: 'image',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Imagem" />
    ),
    cell: ({ row }) => (
      <Avatar>
        <AvatarImage className="object-cover" src={row.getValue('image')} />
        <AvatarFallback>?</AvatarFallback>
      </Avatar>
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    title: 'Nome',
    accessorKey: 'name',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Nome" />
    ),
    cell: ({ row }) => (
      <Link to={row.original.ROWID}>
        <div className="underline lg:w-64">{row.getValue('name')}</div>
      </Link>
    ),
    enableSorting: true,
    enableHiding: false,
    filterFn: (row, id, value) => {
      return filterIgnoreAccents(row.getValue(id), value);
    },
    filterType: TABLE_FILTER_LIKE.BOTH,
  },
  {
    title: 'Categoria',
    accessorKey: 'type',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Categoria" />
    ),
    cell: ({ row }) => {
      return <div>{row.getValue('type')}</div>;
    },
    enableSorting: false,
    enableHiding: true,
  },
  {
    title: 'Estado',
    accessorKey: 'status',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Estado" />
    ),
    cell: ({ row }) => {
      return row.getValue('status') ? (
        <Badge variant="success">Ativa</Badge>
      ) : (
        <Badge variant="destructive">Inativa</Badge>
      );
    },
  },
  {
    title: 'Data de Criação',
    accessorKey: 'createdTime',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Data de Criação" />
    ),
    cell: ({ row }) => {
      const dateString = row.getValue('createdTime');

      if (!dateString) {
        return <div>---</div>;
      }

      try {
        const inputFormat = 'yyyy-MM-dd HH:mm:ss:SSS';
        const dateObject = parse(dateString, inputFormat, new Date());

        return <div>{format(dateObject, 'dd-MM-yyyy HH:mm:ss')}</div>;
      } catch (error) {
        console.error('Failed to parse date:', dateString, error);

        return <div>---</div>;
      }
    },
    enableSorting: true,
    enableHiding: true,
  },
  {
    id: 'actions',
    cell: ({ row }) => {
      return (
        <div className="flex flex-row space-x-2">
          <Link to={row?.original?.ROWID}>
            <Button
              variant="outline"
              className="h-auto p-2"
              aria-label="Ver loja"
            >
              <EyeIcon />
            </Button>
          </Link>
          <Link to={`${row?.original?.ROWID}/edit`}>
            <Button
              variant="outline"
              className="h-auto p-2"
              aria-label="Editar loja"
            >
              <EditIcon />
            </Button>
          </Link>
        </div>
      );
    },
  },
];

export function ShopsList() {
  const [exportDialogOpen, setExportDialogOpen] = useState(false);

  const {
    table,
    query: queryShops,
    filters,
  } = useTable(
    TABLE_COLUMNS,
    {
      queryKey: ['shops'],
      queryFn: async () => {
        const { data: shops } = await msCatalystApi.get(
          `/shops/paginate?${filters}`,
        );

        return shops.data;
      },
      placeholderData: keepPreviousData,
      refetchOnWindowFocus: false,
      select: (data) => {
        return {
          ...data,
          rows: data.rows.map((item) => ({
            ...item,
            id: item.ROWID,
            createdTime: item.CREATEDTIME,
          })),
        };
      },
    },
    {
      sorting: [
        {
          id: 'createdTime',
          desc: true,
        },
      ],
    },
  );

  const mutationExportData = useMutation({
    mutationFn: async (fileType) => {
      try {
        return getExportFile('/shops/export', 'shops', { fileType });
      } catch (error) {
        throw error;
      }
    },
    onError: () => {
      toast.error('Erro ao exportar Lojas', {
        description: 'Tente novamente mais tarde',
      });
    },
    onSuccess: () => {
      toast.success('Exportação realizada com sucesso', {
        description:
          'Foi automaticamente realizado o download do ficheiro .xlsx',
      });
    },
    onSettled: () => {
      setExportDialogOpen(false);
    },
  });

  return (
    <>
      <div className="flex flex-col items-start justify-start gap-y-6 pb-6 lg:flex-row lg:items-center lg:justify-between lg:gap-y-0">
        <Heading>Lojas</Heading>

        <div className="flex flex-row flex-wrap justify-end gap-4">
          <DialogExportRecords
            exportDialogOpen={exportDialogOpen}
            setExportDialogOpen={setExportDialogOpen}
            action={mutationExportData.mutate}
            description="O processo de exportação abrange toda a informação presente das Lojas na base de
          dados."
          />

          <Button asChild>
            <Link to="import">
              <UploadIcon />
              Importar
            </Link>
          </Button>

          <Button asChild>
            <Link to="create">
              <PlusIcon />
              Criar
            </Link>
          </Button>
        </div>
      </div>

      {queryShops.isLoading || queryShops.isPending ? (
        <div className="flex h-[70dvh] items-center justify-center">
          <Loading />
        </div>
      ) : queryShops.isError ? (
        <Error />
      ) : (
        <div className="space-y-4">
          {/* TABLE HEADER */}
          <DataTableToolbar
            table={table}
            actions={{ route: 'shops', useQuery: queryShops }}
          >
            <DebouncedInput
              placeholder="Pesquisar nome..."
              value={table.getColumn('name')?.getFilterValue() ?? ''}
              onChange={(val) => table.getColumn('name')?.setFilterValue(val)}
              className="h-auto w-[150px] lg:w-[255px]"
            />
            {table.getColumn('status') && (
              <DataTableFacetedFilter
                column={table.getColumn('status')}
                title="Estado"
                options={[
                  { label: 'Ativa', value: true },
                  { label: 'Inativa', value: false },
                ]}
              />
            )}
          </DataTableToolbar>
          {/* TABLE HEADER */}

          {/* TABLE */}
          <div className="rounded-md border border-muted-foreground/35">
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      return (
                        <TableHead key={header.id} colSpan={header.colSpan}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                        </TableHead>
                      );
                    })}
                  </TableRow>
                ))}
              </TableHeader>

              <TableBody>
                {queryShops.isRefetching ? (
                  <TableRow>
                    <TableCell
                      colSpan={TABLE_COLUMNS.length}
                      className="h-24 text-center"
                    >
                      <Loading />
                    </TableCell>
                  </TableRow>
                ) : queryShops.isRefetchError ? (
                  <TableRow>
                    <TableCell
                      colSpan={TABLE_COLUMNS.length}
                      className="h-24 text-center"
                    >
                      <Error />
                    </TableCell>
                  </TableRow>
                ) : table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.id}
                      data-state={row.getIsSelected() && 'selected'}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : table.getState().columnFilters.length ? (
                  <TableRow>
                    <TableCell
                      colSpan={TABLE_COLUMNS.length}
                      className="h-24 text-center"
                    >
                      Não foram encontrados resultados.
                    </TableCell>
                  </TableRow>
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={TABLE_COLUMNS.length}
                      className="h-24 text-center"
                    >
                      {table.getState().columnFilters.length
                        ? 'Não foram encontrados resultados.'
                        : 'Não existem quaisquer Lojas na base de dados.'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          {/* TABLE */}

          {/* TABLE FOOTER */}
          <DataTableFooter table={table} />
          {/* TABLE FOOTER */}
        </div>
      )}
    </>
  );
}
